import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, User, Mail, Lock } from "lucide-react";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/34843d89-c4c7-4c13-a5be-c652c2b01586/images/dad7a5fe280cd9cceb321bca2f61bf5239b033bcc313608c789f75a4791a3649.png";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Account created! Let's register your school 🎉");
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen relative grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-12 bg-brand-purpleLight">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="relative max-w-sm text-center">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-white flex items-center justify-center shadow-lg mb-6">
            <GraduationCap className="w-8 h-8 text-brand-purple" />
          </div>
          <h1 className="font-heading text-4xl font-semibold text-foreground">Become a SchoolBoss.</h1>
          <p className="mt-4 text-brand-inkMuted">
            Create your owner account, then register your first school. Our admins review every school for quality.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background relative">
        <div className="absolute top-6 left-6">
          <Link to="/" className="flex items-center" data-testid="register-back-home">
            <img
              src="/brand/logo-day.png"
              alt="SchoolBoss"
              className="h-10 w-auto select-none"
              draggable="false"
            />
          </Link>
        </div>

        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6 mt-12" data-testid="register-form">
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">Create your account</h2>
            <p className="text-brand-inkMuted mt-2">
              Already a boss?{" "}
              <Link to="/login" className="text-brand-purple font-semibold hover:underline" data-testid="register-go-login">
                Log in
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="font-heading text-foreground">Your name</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  id="name" required value={form.name} onChange={update("name")}
                  placeholder="Jamie Educator"
                  className="rounded-2xl h-12 pl-10 border-brand-purple/20 focus-visible:ring-brand-purple"
                  data-testid="register-name-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="font-heading text-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  id="email" type="email" required value={form.email} onChange={update("email")}
                  placeholder="you@school.com"
                  className="rounded-2xl h-12 pl-10 border-brand-purple/20 focus-visible:ring-brand-purple"
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="font-heading text-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  id="password" type="password" required minLength={6} value={form.password} onChange={update("password")}
                  placeholder="At least 6 characters"
                  className="rounded-2xl h-12 pl-10 border-brand-purple/20 focus-visible:ring-brand-purple"
                  data-testid="register-password-input"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <Button
            type="submit" disabled={loading}
            className="w-full h-12 rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating account..." : "Create account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
